import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, SlidersHorizontal, LogOut, Check, X, CornerUpLeft,
  ChevronDown, Clock, Users, Bot, Archive, User as UserRound,
} from "lucide-react";
import { Avatar, Badge, Button, cn } from "../ui";
import ChannelLogo from "../ChannelLogo";
import {
  CHANNEL_LABELS, channelOf, isLive, isWaiting, liveChatStatusLabel, shortAgo, toMs, visitorName,
} from "../../utils/liveChat";

export const TABS = [
  { id: "waiting", label: "Waiting", hint: "Visitors asking for an agent", icon: Clock },
  { id: "mine", label: "My chats", hint: "Chats assigned to you", icon: UserRound },
  { id: "team", label: "Team chats", hint: "All chats handled by agents", icon: Users },
  { id: "bot", label: "With the bot", hint: "The bot is answering", icon: Bot },
  { id: "closed", label: "Closed", hint: "Resolved and ended chats", icon: Archive },
];

// count next to a view; Waiting is highlighted while anyone waits
const CountPill = ({ id, n }) => (
  <span className={cn(
    "min-w-[22px] shrink-0 rounded-md px-1.5 py-0.5 text-center text-[11px] font-medium tabular-nums",
    id === "waiting" && n > 0 ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600"
  )}>
    {n === undefined ? "-" : n > 999 ? "999+" : n}
  </span>
);

export const OPEN_SEARCH_EVENT = "easychat:open-search";

const SORTS = [
  { value: "default", label: "Default order" },
  { value: "latest", label: "Latest activity" },
  { value: "oldest", label: "Oldest activity" },
  { value: "name", label: "Name (A-Z)" },
];

export const ChannelIcon = ({ session, className }) => {
  const channel = channelOf(session);
  return <ChannelLogo channel={channel} label={CHANNEL_LABELS[channel]} className={className} />;
};

// Waiting time: grey up to 1 minute, amber up to 3, red after that
const WaitBadge = ({ session, now }) => {
  const since = toMs(session.handoff_requested_at) || toMs(session.updated_at);
  const secs = since ? (now - since) / 1000 : 0;
  const variant = secs > 180 ? "danger" : secs > 60 ? "warning" : "default";
  return <Badge variant={variant} className="tabular-nums">{shortAgo(since, now) === "now" ? "<1m" : shortAgo(since, now)}</Badge>;
};

const Row = ({ session, active, unread, now, onSelect }) => {
  const name = visitorName(session);
  // attachments as words, not their file paths
  const preview = String(session.last_message || session.summary || "")
    .replace(/::IMAGE::\S+/g, "[Image]").replace(/\[File: ([^\]]+)\]\([^)]*\)/g, "[File] $1").replace(/\s+/g, " ").trim();
  const fromVisitor = session.last_message_role === "user";
  return (
    <button
      type="button"
      onClick={() => onSelect(session)}
      className={cn(
        "group flex w-full items-start gap-3 border-b border-zinc-100 px-3 py-3 text-left transition-colors",
        active ? "bg-zinc-100" : "hover:bg-zinc-50"
      )}
    >
      <Avatar name={name} seed={session.session_id} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("truncate text-[13px] text-zinc-900", unread ? "font-semibold" : "font-medium")}>{name}</span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {isWaiting(session) ? (
              <WaitBadge session={session} now={now} />
            ) : (
              <span className="text-[11px] tabular-nums text-zinc-400">{shortAgo(toMs(session.updated_at), now)}</span>
            )}
            {unread && <span className="h-2 w-2 rounded-full bg-sky-500" aria-label="Unread" />}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <ChannelIcon session={session} />
          {preview && !fromVisitor && <CornerUpLeft className="h-3 w-3 shrink-0 text-zinc-400" aria-label="Reply" />}
          <span className={cn("truncate text-xs", unread ? "text-zinc-700" : "text-zinc-500")}>
            {preview || "No messages yet"}
          </span>
        </div>
        {(isLive(session) || session.rating) && (
          <div className="mt-1.5 flex items-center gap-1.5">
            {isLive(session) && (
              <Badge variant="success">{session.assigned_to_name ? `Live · ${session.assigned_to_name}` : liveChatStatusLabel(session.handoff_status)}</Badge>
            )}
            {session.rating ? <Badge variant="outline">Rated {session.rating}/5</Badge> : null}
          </div>
        )}
      </div>
    </button>
  );
};

const EMPTY_TEXT = {
  waiting: "No one is waiting for an agent.",
  mine: "You have no open chats.",
  team: "No chats are being handled by agents.",
  bot: "No conversations with the bot right now.",
  closed: "No closed chats yet.",
};

const ChatList = ({
  sessions, activeSessionId, onSelect, view, onViewChange, counts, isLoading, hasMore, onLoadMore,
  unreadIds, now, team, user, onLogout, searchRef,
}) => {
  const [query, setQuery] = useState("");
  const [channel, setChannel] = useState("all");
  const [sort, setSort] = useState("default");
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const filterRef = useRef(null);
  const viewRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
      if (viewRef.current && !viewRef.current.contains(e.target)) setViewOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // "/" on the screen opens the search box
  useEffect(() => {
    const open = () => setSearchOpen(true);
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => window.removeEventListener(OPEN_SEARCH_EVENT, open);
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = sessions.filter((s) => {
      if (channel !== "all" && channelOf(s) !== channel) return false;
      if (!q) return true;
      return [visitorName(s), s.user_email, s.user_phone, s.last_message, s.summary, s.session_id]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
    if (sort === "latest") list = [...list].sort((a, b) => toMs(b.updated_at) - toMs(a.updated_at));
    if (sort === "oldest") list = [...list].sort((a, b) => toMs(a.updated_at) - toMs(b.updated_at));
    if (sort === "name") list = [...list].sort((a, b) => visitorName(a).localeCompare(visitorName(b)));
    return list;
  }, [sessions, query, channel, sort]);

  const filtersActive = channel !== "all" || sort !== "default";
  const current = TABS.find((t) => t.id === view) || TABS[0];
  const CurrentIcon = current.icon;
  // reminder on the other views while visitors wait
  const waitingElsewhere = view !== "waiting" && counts ? counts.waiting || 0 : 0;
  const onlineCount = team ? team.online_count : null;
  const totalAgents = team && team.agents ? team.agents.length : null;

  return (
    <aside className="flex w-[320px] shrink-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="border-b border-zinc-200 px-3 py-3">
        <div className="flex items-center gap-1.5">
          <div ref={viewRef} className="relative min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setViewOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={viewOpen}
              className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-zinc-100"
            >
              <CurrentIcon className="h-4 w-4 shrink-0 text-zinc-500" />
              <span className="truncate text-sm font-semibold text-zinc-900">{current.label}</span>
              <CountPill id={view} n={counts ? counts[view] : undefined} />
              <ChevronDown className={cn("ml-auto h-4 w-4 shrink-0 text-zinc-400 transition-transform", viewOpen && "rotate-180")} />
            </button>
            {viewOpen && (
              <div role="listbox" className="absolute left-0 top-10 z-40 w-64 rounded-lg border border-zinc-200 bg-white p-1 shadow-lg">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="option"
                      aria-selected={view === t.id}
                      onClick={() => { onViewChange(t.id); setViewOpen(false); }}
                      className={cn("flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-zinc-100", view === t.id && "bg-zinc-100")}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-zinc-500" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-zinc-900">{t.label}</span>
                        <span className="block truncate text-[11px] text-zinc-500">{t.hint}</span>
                      </span>
                      <CountPill id={t.id} n={counts ? counts[t.id] : undefined} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <Button variant={searchOpen || query ? "secondary" : "ghost"} size="icon" aria-label="Search"
            onClick={() => { if (searchOpen) setQuery(""); setSearchOpen((o) => !o); }}>
            <Search className="h-4 w-4" />
          </Button>
          <div ref={filterRef} className="relative">
            <Button variant={filtersActive ? "secondary" : "ghost"} size="icon" onClick={() => setFilterOpen((o) => !o)} aria-label="Filter and sort">
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
            {filterOpen && (
              <div className="absolute right-0 top-10 z-40 w-56 rounded-lg border border-zinc-200 bg-white p-1 shadow-lg">
                <div className="px-2 pb-1 pt-1.5 text-[11px] font-medium text-zinc-400">Channel</div>
                {["all", "webchat", "whatsapp", "instagram", "telegram", "slack", "email"].map((c) => (
                  <button key={c} type="button" onClick={() => setChannel(c)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs text-zinc-700 hover:bg-zinc-100">
                    {c === "all" ? "All channels" : CHANNEL_LABELS[c]}
                    {channel === c && <Check className="h-3.5 w-3.5" />}
                  </button>
                ))}
                <div className="my-1 h-px bg-zinc-100" />
                <div className="px-2 pb-1 pt-1 text-[11px] font-medium text-zinc-400">Sort</div>
                {SORTS.map((o) => (
                  <button key={o.value} type="button" onClick={() => setSort(o.value)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs text-zinc-700 hover:bg-zinc-100">
                    {o.label}
                    {sort === o.value && <Check className="h-3.5 w-3.5" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {(searchOpen || query) && (
          <div className="mt-2 flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 focus-within:border-zinc-400 focus-within:bg-white">
            <Search className="h-3.5 w-3.5 text-zinc-400" />
            <input
              ref={searchRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setQuery(""); setSearchOpen(false); } }}
              placeholder="Search name, email or message"
              className="w-full bg-transparent text-xs text-zinc-900 outline-none placeholder:text-zinc-400"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="text-zinc-400 hover:text-zinc-700">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
        {waitingElsewhere > 0 && (
          <button type="button" onClick={() => onViewChange("waiting")}
            className="mt-2 flex w-full items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-left text-xs text-amber-800 hover:bg-amber-100">
            <Clock className="h-3.5 w-3.5" />
            {waitingElsewhere === 1 ? "1 visitor is" : `${waitingElsewhere} visitors are`} waiting for an agent
            <span className="ml-auto font-medium">View</span>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {isLoading && !sessions.length ? (
          <div className="space-y-3 p-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex animate-pulse gap-3">
                <div className="h-9 w-9 rounded-full bg-zinc-100" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 w-1/2 rounded bg-zinc-100" />
                  <div className="h-3 w-4/5 rounded bg-zinc-100" />
                </div>
              </div>
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-zinc-700">{query || channel !== "all" ? "No matching chats" : "All clear"}</p>
            <p className="mt-1 text-xs text-zinc-500">{query || channel !== "all" ? "Try another search or filter." : EMPTY_TEXT[view]}</p>
          </div>
        ) : (
          shown.map((s) => (
            <Row key={s.session_id} session={s} active={activeSessionId === s.session_id}
              unread={unreadIds.has(s.session_id)} now={now} onSelect={onSelect} />
          ))
        )}
        {hasMore && !query && (
          <div className="p-3">
            <Button variant="outline" size="sm" className="w-full justify-center" onClick={onLoadMore} disabled={isLoading}>
              {isLoading ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-3 py-2.5 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1.5">
          <span className={cn("h-2 w-2 rounded-full", onlineCount > 0 ? "bg-emerald-500" : "bg-zinc-300")} />
          {onlineCount === null ? "Checking agents..." : `${onlineCount} of ${totalAgents} agents online`}
        </span>
        {onLogout && user && user.role !== "admin" && user.role !== "super_admin" && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={onLogout}>
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        )}
      </div>
    </aside>
  );
};

export default ChatList;
